import { createContext } from 'react';
import type { GateDecisionValue } from '@api/processRuns';
import type { NodeRunGateState } from './runStatus/upstreamGating';
import type { ProcessNodeData } from './graphTypes';
import type { NodeLiveState } from './useProcessRunSession';
import type { ValidationError } from './validation/types';
import type { WireStyle } from './useWireStyle';
import type { ConnectionFocus } from './connectionFocus';

/**
 * Minimal descriptor of a gate that is currently waiting on a human
 * decision, exposed to leaf node renderers. Carries just the iteration
 * so the consumer can build a stable key — full state stays inside
 * {@link useProcessRunSession}.
 */
export interface GateWaitingInfo {
  readonly iteration: number;
}

/**
 * Unified per-node display state derived from (a) the live SSE-driven
 * session state, when present, and (b) the persistent run snapshot
 * carried on {@code node.data.latestExecution} / {@code latestArtifacts}.
 * The renderer treats this as a single source of truth so it doesn't
 * need to know which layer the data came from.
 *
 * The {@code source} discriminator is exposed for components that want to
 * render slightly differently (e.g. a pulsing dot for live runs vs a
 * static checkmark for the persistent snapshot).
 */
export interface NodeDisplayState {
  readonly status: 'idle' | 'running' | 'completed' | 'failed' | 'waiting' | 'skipped';
  readonly errorMessage: string | null;
  /** Raw executor text behind {@link errorMessage}; null when there is
   *  nothing beyond the message itself to show. */
  readonly errorDetail: string | null;
  /** Most recent execution time (ms) — only present for completed / failed states. */
  readonly executionTimeMs: number | null;
  /** Credits billed for the most recent attempt; null/absent when not billed. */
  readonly creditsSpent?: number | null;
  readonly source: 'live' | 'persisted' | 'none';
  /** Live state (when {@code source === 'live'}) so callers can read iteration / artifacts. */
  readonly live?: NodeLiveState;
}

/**
 * Bridge so the React-Flow custom node renderers can talk back to
 * {@code useProcessGraph} and {@code useProcessRunSession} without
 * prop-drilling through the React Flow internals.
 *
 * Keep the surface tight — only the operations a leaf node actually needs.
 * `getRunGate` + `runStateForNode` are the two read-paths;
 * `startNodeRun` + `cancelNodeRun` are the two write-paths.
 */
export interface EditorContextValue {
  /** Patch a node's `data` shape. The caller passes the right per-type shape. */
  updateNodeData: (id: string, patch: Partial<ProcessNodeData>) => void;
  /** Drop the node and any edges that touch it. */
  removeNode: (id: string) => void;
  /**
   * Clone a single node at a fixed offset, with fresh ids on the clone
   * + its ports. Sibling to {@link duplicateSelectedNodes} that operates
   * on the user's selection — this one is targeted by id so the per-node
   * 3-dot menu / floating action chip can call it without forcing a
   * selection state change first.
   */
  duplicateNode: (id: string) => void;
  /**
   * Whether the editor is currently read-only. True for published
   * processes AND while a test run is in flight (the editor is frozen
   * with a banner so the user can't mutate the graph mid-run).
   */
  readOnly: boolean;

  // ── Run integration ─────────────────────────────────────────────────────
  /**
   * Merged accessor for per-node run display state. Consumers should NOT
   * read {@code node.data.latestExecution} directly — this accessor
   * already overlays live state on top, so the badge a node renders is
   * always the freshest available signal.
   */
  runStateForNode: (nodeId: string) => NodeDisplayState;
  /**
   * Per-node gate descriptor — `canRun: true` means the Run button is
   * enabled; otherwise the descriptor carries the reason for the disable
   * (so the renderer can render a useful tooltip explaining whether the
   * node is mis-configured, an upstream hasn't run, or another run is in
   * progress globally).
   */
  getRunGate: (nodeId: string) => NodeRunGateState;
  /**
   * Validation errors attached to a specific node. Drives the per-node
   * "this needs attention" surface — red border on the card, "Check"
   * affordance on the run button, tooltip explaining the issue. Returns
   * an empty array for healthy nodes (and for graph-level errors which
   * have a null `nodeId`).
   */
  errorsForNode: (nodeId: string) => readonly ValidationError[];
  /**
   * Findings attached to a specific node that block publishing but not
   * running it (an unwired port, a node off the input→output path). Kept
   * apart from {@link errorsForNode} so they paint the amber "wire me up"
   * ring without disabling the node's Run button — a half-wired graph is
   * runnable, and the backend agrees.
   */
  publishHintsForNode: (nodeId: string) => readonly ValidationError[];
  /**
   * True while a run that will (re)compute {@code nodeId} is in flight —
   * reliable through the window where the live map has been cleared on
   * run start but the SSE seed hasn't landed yet (unlike the per-node
   * resolved status, which falls back to the stale persisted snapshot).
   * Drives the generation nodes' "generating" overlay + their
   * forward-only preview hold.
   */
  isNodeGenerating: (nodeId: string) => boolean;
  /**
   * True while a run is starting or in flight, regardless of which node the
   * engine is currently working. Unlike {@link isNodeGenerating} this stays
   * true after a node has finished, which is what artifact cards need to
   * decide whether a server-side derived preview may still be on its way.
   */
  readonly isRunActive: boolean;
  /** Kick off a single-node test run. No-op if {@code getRunGate(id).canRun === false}. */
  startNodeRun: (nodeId: string) => void;
  /** Cancel the currently-running node (if any). */
  cancelNodeRun: () => void;
  /**
   * Returns the gate-waiting descriptor for {@code nodeId} when the
   * active run is paused on that gate, or {@code null} otherwise.
   * Lets the node card light up its Approved / Rejected branch
   * affordances without subscribing to the full session state.
   */
  getGateWaitingForNode: (nodeId: string) => GateWaitingInfo | null;
  /**
   * Reopen the gate decision modal for a gate that is currently
   * waiting. Called from in-canvas affordances (e.g. clicking the
   * Approved / Rejected pill on the gate node) after the user
   * dismissed the dialog with "Decide later". When {@code decision} is
   * passed, the modal opens with that decision pre-selected so the
   * user can confirm with one extra click (still with the optional
   * comment field). The modal remains the single decision surface;
   * the backend authorization is unchanged.
   */
  requestGateDecision: (nodeId: string, decision?: GateDecisionValue) => void;
  /**
   * Refresh the presigned download URL for an artifact (URLs expire after
   * a short window). Returns the fresh URL the caller should navigate to.
   * Reused by Output node + future Runs page.
   */
  refreshArtifactUrl: (artifactId: string) => Promise<string>;

  // ── Canvas toolbar surface ─────────────────────────────────────────────
  // Everything the floating top + bottom canvas toolbars consume lives
  // here so the toolbar components stay presentational. The page wires
  // these from the underlying graph hook + per-page UI state.

  /** Whether the Run History sheet is currently displayed over the canvas. */
  readonly runHistoryOpen: boolean;
  /** Toggle / set the Run History sheet open/closed. Published-only affordance. */
  setRunHistoryOpen: (open: boolean) => void;

  /** Per-node UI-only collapsed state (header-only render when true). */
  isNodeCollapsed: (nodeId: string) => boolean;
  /** Flip a single node's collapsed state. */
  toggleNodeCollapsed: (nodeId: string) => void;
  /** Force every node id into the same collapsed state (Collapse-all / Expand-all). */
  setAllNodesCollapsed: (collapsed: boolean) => void;

  /** Active wire renderer style. Drives `BranchedEdge`'s path generator. */
  readonly wireStyle: WireStyle;
  /** Update the active wire style. Persisted to localStorage. */
  setWireStyle: (next: WireStyle) => void;

  /** Whether the dotted background grid is currently visible. */
  readonly gridOn: boolean;
  setGridOn: (next: boolean) => void;
  /** Whether the bottom-right minimap is currently visible. */
  readonly miniOn: boolean;
  setMiniOn: (next: boolean) => void;

  // ── Undo / redo ────────────────────────────────────────────────────────
  readonly canUndo: boolean;
  readonly canRedo: boolean;
  undo: () => void;
  redo: () => void;

  // ── Selection-aware Edit group ─────────────────────────────────────────
  /** Currently-selected node ids -- drives the Duplicate / Delete buttons. */
  readonly selectedNodeIds: readonly string[];
  /** Currently-selected edge ids -- enables Delete on bare-edge selections. */
  readonly selectedEdgeIds: readonly string[];
  /** Wires and nodes attached to the selected nodes; null when nothing should dim. */
  readonly connectionFocus: ConnectionFocus | null;
  /**
   * Drop every currently-selected node AND edge in one history frame —
   * incident edges are dropped along with their nodes, plus any edges
   * the user explicitly selected on their own (AIB-7081).
   */
  removeSelectedNodes: () => void;
  /** Duplicate every selected node at a fixed offset. */
  duplicateSelectedNodes: () => void;

  // ── Layout ─────────────────────────────────────────────────────────────
  /** Run dagre LR auto-arrange + apply the resulting positions. */
  autoArrange: () => void;

  // ── React-Flow viewport bridge ─────────────────────────────────────────
  /** Zoom in by one step. */
  zoomIn: () => void;
  /** Zoom out by one step. */
  zoomOut: () => void;
  /** Fit every node in the current viewport. */
  fitView: () => void;
  /** Recentre the viewport on the graph's bounding-box centre. */
  recenter: () => void;

  // ── Test-run / Clear-statuses passthrough ──────────────────────────────
  /** True when ANY node has a non-idle run state (drives Clear-all visibility). */
  readonly hasNodeRunStates: boolean;
  /** Clear every per-node draft run status + remove draft artifacts. */
  clearNodeStatuses: () => void;
}

export const EditorContext = createContext<EditorContextValue | null>(null);

export const EditorContextProvider = EditorContext.Provider;
